/**
 ShapeDiver 3D Viewer Application

 MaterialPresets.js

 Content:
   Library of material presets and handler function

  Copyright (c) 2018 ShapeDiver GmbH

  @module MaterialPresets
 */

// TODO rewrite this to not include hardcoded material presets, but load them from our central material library

/**
 * Import three
 */
const THREE = require('../../externals/three');

/**
 * Global utilities
 */
const GLOBAL_UTILS = require('../util/GlobalUtils');

/**
 * Location of material textures
 */
const TEXTURE_PATH = 'https://viewer.shapediver.com/v2/materials/';

/**
 * Default resolution, available resolutions
 */
const RESOLUTIONS = ['256', '512', '1024', '2048'];
const DEFAULTRESOLUTION = '1024'; // FIXME make it configurable

/**
 * @classdesc Material preset container singleton
 * @class
 * @hideconstructor
 */
let MaterialPresets = function () {

  let that = this;

  /**
   * Object containing the library of known materials
   */
  let _materials = {};

  /**
   * Helper function for casting id representation to the one we use internally
   */
  let _castId = function(id) {
    if (typeof id !== 'string') id = String(id);
    return id.padStart(2, '0').slice(0, 2);
  };

  /**
   * Helper function for adding statically defined materials
   */
  let _addMaterial = function(name, classId, specificId, mat) {

    // make sure classId and specificId are given in the format we use internally
    classId = _castId(classId);
    specificId = _castId(specificId);

    // default material version
    mat.version = mat.version || '2.0';

    // add material definition to library
    if ( !_materials.hasOwnProperty(classId) ) _materials[classId] = {};
    _materials[classId][specificId] = { name: name, mat: mat };
  };

  /**
   * Check if material exists
   */
  that.hasMaterial = function(classId, specificId) {
    // make sure classId and specificId are given in the format we use internally
    classId = _castId(classId);
    specificId = _castId(specificId);
    // check library
    if ( !_materials.hasOwnProperty(classId) ) return false;
    if ( !_materials[classId].hasOwnProperty(specificId) ) return false;
    return true;
  };

  /**
   * Get material
   */
  that.getMaterial = function(classId, specificId, resolution) {
    // make sure classId and specificId are given in the format we use internally
    classId = _castId(classId);
    specificId = _castId(specificId);
    resolution = resolution || DEFAULTRESOLUTION;
    // get material
    let mat = GLOBAL_UTILS.deepCopy(_materials[classId][specificId].mat);
    // set texture properties according to resolution and texture path
    let basePath = TEXTURE_PATH + resolution + '/' + classId + '/' + specificId + '/';
    ['bitmap', 'metalness', 'roughness', 'bump', 'normal', 'transparency'].forEach( t => {
      let tt = t + 'texture';
      if (mat.hasOwnProperty(tt)) {
        mat[tt] = basePath + mat[tt];
      }
    });

    return mat;
  };

  /**
   * Default material, used if no material is defined
   *
   * Id: 0100
   */
  _addMaterial('defaultMaterial', 0, 0, {
    color: [211, 211, 211, 255],
    bumpAmplitude: 1.0,
    roughness: 1.0,
    metalness: 0.0
  });

  /**
   * Groundplane material, used only for scene groundplane object
   *
   * Id: 0101
   */
  _addMaterial('groundplaneMaterial', 1, 1, {
    color: [211, 211, 211, 255],
    roughness: 1.0,
    metalness: 0.0,
    side: THREE.FrontSide
  });

  /**
   * Default plastic material
   *
   * Id: 0200
   */
  _addMaterial('defaultPlasticMaterial', 2, 0, {
    color: [211, 211, 211, 255],
    roughness: 1.0,
    metalness: 0.0,
    normaltexture: 'normalMap.jpg',
    roughnesstexture: 'roughnessMap.jpg'
  });

  /**
   * Default metal material
   *
   * Id: 0300
   */
  _addMaterial('defaultMetalMaterial', 3, 0, {
    color: [205, 205, 205, 255],
    roughness: 0.25,
    metalness: 1.0
  });

  /**
   * Used metal material
   *
   * Id: 0301
   */
  _addMaterial('usedMetalMaterial', 3, 1, {
    color: [205, 205, 205, 255],
    roughness: 1.0,
    metalness: 1.0,
    roughnesstexture: 'roughnessMap.jpg'
  });

  /**
   * Metal material varation 1
   *
   * Id: 0302
   */
  _addMaterial('metalVariation1Material', 3, 2, {
    color: [205, 205, 205, 255],
    roughness: 1.0,
    metalness: 1.0,
    roughnesstexture: 'roughnessMap.jpg'
  });

  /**
   * Metal material varation 2
   *
   * Id: 0303
   */
  _addMaterial('metalVariation2Material', 3, 3, {
    color: [205, 205, 205, 255],
    roughness: 1.0,
    metalness: 1.0,
    roughnesstexture: 'roughnessMap.jpg'
  });

  /**
   * Gold material
   *
   * Id: 0310
   */
  _addMaterial('goldMaterial', 3, 10, {
    color: [230, 207, 92, 255],
    roughness: 0.0,
    metalness: 1.0
  });

  /**
   * Used gold material
   *
   * Id: 0311
   */
  _addMaterial('usedGoldMaterial', 3, 11, {
    color: [230, 207, 92, 255],
    roughness: 1.0,
    metalness: 1.0,
    roughnesstexture: 'roughnessMap.jpg'
  });

  /**
   * Default glass material
   *
   * Id: 0400
   */
  _addMaterial('defaultGlassMaterial', 4, 0, {
    color: [211, 211, 211, 255],
    roughness: 0.0,
    metalness: 1.0,
    transparency: 0.75
  });

  /**
   * Default wood material
   *
   * Id: 0500
   */
  _addMaterial('defaultWoodMaterial', 5, 0, {
    color: [211, 211, 211, 255],
    roughness: 1.0,
    metalness: 0.0,
    bitmaptexture: 'map.jpg',
    normaltexture: 'normalMap.jpg',
    metalnesstexture: 'metalnessMap.jpg',
    roughnesstexture: 'roughnessMap.jpg'
  });

  /**
   * Wood floor material
   *
   * Id: 0501
   */
  _addMaterial('woodFloorMaterial', 5, 1, {
    color: [211, 211, 211, 255],
    roughness: 1.0,
    metalness: 0.0,
    bitmaptexture: 'map.jpg',
    normaltexture: 'normalMap.jpg',
    metalnesstexture: 'metalnessMap.jpg',
    roughnesstexture: 'roughnessMap.jpg'
  });

  /**
   * Natural oak material
   *
   * Id: 0510
   */
  _addMaterial('naturalOakMaterial', 5, 10, {
    roughness: 1.0,
    metalness: 0.0,
    bitmaptexture: 'map.jpg',
    normaltexture: 'normalMap.jpg',
    roughnesstexture: 'roughnessMap.jpg'
  });

  /**
   * Natural oak material
   *
   * Id: 0511
   */
  _addMaterial('premiumOakMaterial', 5, 11, {
    roughness: 1.0,
    metalness: 0.0,
    bitmaptexture: 'map.jpg',
    roughnesstexture: 'roughnessMap.jpg'
  });


  /**
   * Shortcut function to create leather material presets
   */


  let _addLeather = function (name, leatherId) {
    _addMaterial(name, 6, leatherId, {
      roughness: 1.0,
      metalness: 0.0,
      bitmaptexture: 'map.jpg',
      normaltexture: 'normalMap.jpg',
      metalnesstexture: 'metalnessMap.jpg',
      roughnesstexture: 'roughnessMap.jpg'
    });
  };


  /**
   * Default leather material
   *
   * Id: 0600
   */
  _addLeather('defaultLeatherMaterial', 0);

  /**
   * Dark brown leather material
   *
   * Id: 0601
   */
  _addLeather('darkBrownLeatherMaterial', 1);

  /**
   * Black leather material
   *
   * Id: 0602
   */
  _addLeather('blackLeatherMaterial', 2);


  /**
   * Worn leather material
   *
   * Id: 0610
   */
  _addLeather('wornLeatherMaterial', 10);

  /**
   * Default fabric material
   *
   * Id: 0700
   */
  _addMaterial('defaultFabricMaterial', 7, 0, {
    roughness: 1.0,
    metalness: 1.0,
    bitmaptexture: 'map.jpg',
    normaltexture: 'normalMap.jpg',
    roughnesstexture: 'roughnessMap.jpg',
    metalnesstexture: 'metalnessMap.jpg'
  });

  /**
   * Grey fabric material
   *
   * Id: 0701
   */
  _addMaterial('greyFabricMaterial', 7, 1, {
    roughness: 1.0,
    metalness: 1.0,
    bitmaptexture: 'map.jpg',
    normaltexture: 'normalMap.jpg',
    roughnesstexture: 'roughnessMap.jpg',
    metalnesstexture: 'metalnessMap.jpg'
  });


  /**
   * Return preset material by id
   * @public
   * @function
   * @param  {Integer} id The id of the preset. See table below.
   * @return {module:JSONMaterial~JSONMaterial}    JSON material of version 2.0
   */
  that.fromId = function (id) {

    let resolution;

    // check if we got a resolution as part of the id
    if (typeof id === 'string') {
      let parts = id.split('@');
      id = parts[0];
      if (parts.length > 1) {
        resolution = parts[1];
      }
    }

    if ( !RESOLUTIONS.includes(resolution) ) {
      resolution = DEFAULTRESOLUTION;
    }

    // convert to number
    id = Number(id);

    // for a while, we had documented the presets to be 10, 20, 30 and 40
    // here, we allow for the few cases where this was used to succeed
    if (id < 100 && id % 10 == 0) {
      id /= 10;
    }

    // if the id is less than 10, multiply it by 100
    // if (Math.floor(Math.log(id) / Math.log(10)) == 0) id *= 100;
    if (id < 10) id *= 100;

    // split id into class and specific material
    let classID = Math.floor(id / 100);
    let specificID = id - (classID * 100);

    // check for material
    if ( that.hasMaterial(classID, specificID) ) {
      return that.getMaterial(classID, specificID, resolution);
    }
    else if ( that.hasMaterial(classID, 0) ) {
      return that.getMaterial(classID, 0, resolution);
    }
    else {
      return that.getMaterial(0, 0, resolution);
    }
  };

  return that;
};

// define singleton material library and export it
const MATERIAL_PRESETS = new MaterialPresets();

module.exports = MATERIAL_PRESETS;
